import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { UpdateIcon, CancelButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import { updateSkillxXpRule, getSkillxXpRules } from '../../../redux/features/skillxSlice';

/**
 * Edit an XP rule — this is the "Admin configures XP values" screen's real work.
 *
 * Two things the form has to make plain, because both are surprising:
 *
 *   - `rule_key` is not editable. It is the join key from every XP event ever
 *     awarded; renaming it would orphan that history. Retire the rule and make
 *     a new one instead.
 *   - Changing `points` is not retroactive. Each ledger row stores the value it
 *     was awarded at, so nobody's total moves when a rule is retuned — which is
 *     the intended behaviour, and worth saying before someone expects otherwise.
 *
 * The producers in the student portal cache this table in process, so a change
 * lands there on its next refresh/restart rather than instantly.
 */

const ValidationSchema = Yup.object().shape({
  label: Yup.string().trim().required('Label is required').max(120, 'Label is too long'),
  points: Yup.number()
    .typeError('Points must be a number')
    .integer('Points must be a whole number')
    .min(1, 'Points must be at least 1')
    .max(1000, 'That is a very large award — cap a single rule at 1000')
    .required('Points are required'),
  icon: Yup.string().trim().max(120, 'Icon name is too long'),
});

// Rules owned by an automatic producer. Mirrors PRODUCER_OWNED_RULES in
// skillxXp.controller.js, which enforces it — this copy only explains the
// disabled switch rather than making the decision.
const PRODUCER_OWNED_RULES = [
  'daily_login',
  'live_class',
  'on_time',
  'assignment',
  'assignment_early',
];

export default function UpdateXpRule({ rowData, dispatchparams }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);

  const isProducerOwned = PRODUCER_OWNED_RULES.includes(rowData?.rule_key);
  const eventCount = rowData?.event_count || 0;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label: rowData?.label || '',
      points: rowData?.points ?? 0,
      icon: rowData?.icon || '',
      is_bonus: Boolean(rowData?.is_bonus),
      is_active: Boolean(rowData?.is_active),
      allow_manual_award: Boolean(rowData?.allow_manual_award),
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      dispatch(
        updateSkillxXpRule({
          id: rowData?.id,
          label: values.label,
          points: Number(values.points),
          icon: values.icon || null,
          is_bonus: values.is_bonus,
          is_active: values.is_active,
          allow_manual_award: values.allow_manual_award,
        }),
      ).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getSkillxXpRules(dispatchparams));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Failed to update the rule', {
            variant: 'error',
          });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit, setFieldValue } = formik;
  const pointsChanged = Number(values.points) !== Number(rowData?.points);

  return (
    <GlobalDialog
      title={`Edit — ${rowData?.label || 'XP rule'}`}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <UpdateIcon size="small" action={() => setOpen(true)}>
          Edit
        </UpdateIcon>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Rule key"
                fullWidth
                disabled
                value={rowData?.rule_key || ''}
                helperText="Permanent — every XP event already awarded refers to this key."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="label"
                  label="Activity name"
                  fullWidth
                  value={values.label}
                  onChange={handleChange}
                  error={touched.label && Boolean(errors.label)}
                  helperText={touched.label && errors.label}
                />
                <TextField
                  name="points"
                  label="Points"
                  type="number"
                  fullWidth
                  value={values.points}
                  onChange={handleChange}
                  error={touched.points && Boolean(errors.points)}
                  helperText={touched.points && errors.points}
                />
              </Stack>

              {pointsChanged && eventCount > 0 && (
                <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                  This rule has already awarded <b>{eventCount.toLocaleString()}</b> times. The
                  new value applies to <b>future awards only</b> — XP already earned keeps what
                  it was worth, so no learner&apos;s total or rank will move.
                </Alert>
              )}

              <Box>
                <TextField
                  name="icon"
                  label="Icon (optional)"
                  fullWidth
                  value={values.icon}
                  onChange={handleChange}
                  error={touched.icon && Boolean(errors.icon)}
                  helperText={
                    (touched.icon && errors.icon) ||
                    'An Iconify name, e.g. solar:star-shine-bold-duotone'
                  }
                />
                {/* The field wants a name from a set of ~200,000 — the only
                    practical way to find one is to search Iconify itself. */}
                <Box sx={{ pl: 1.75, pt: 0.25 }}>
                  <Chip
                    size="small"
                    clickable
                    component="a"
                    href="https://icon-sets.iconify.design/"
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<Icon icon="solar:magnifer-bold-duotone" width={13} height={13} />}
                    label="Browse icons on Iconify"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.allow_manual_award}
                      onChange={(e) => setFieldValue('allow_manual_award', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5 }}>
                      Mentors may award this by hand
                    </Typography>
                  }
                />
                {isProducerOwned && (
                  <Typography sx={{ fontSize: 11.5, color: '#66708A', pl: 5.5, mt: -0.5 }}>
                    Awarded automatically by the portal. Award XP lists it either way — this only
                    decides whether it reads as mentor-facing. A hand-award on top of the
                    automatic one credits the same action twice.
                  </Typography>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={values.is_bonus}
                      onChange={(e) => setFieldValue('is_bonus', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5 }}>
                      Bonus — stacks on top of another award
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.is_active}
                      onChange={(e) => setFieldValue('is_active', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5 }}>
                      Active — turn off to retire the rule
                    </Typography>
                  }
                />
              </Box>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
                <UpdateButton disabled={loading} size="small" type="submit">
                  {loading ? 'Please wait...' : 'Update'}
                </UpdateButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}
